import React from 'react';
import { Database, Activity, BarChart3, MessageSquare, Shield, Zap, GraduationCap } from 'lucide-react';
import boxVetrineDefault from '../content/box_vetrine.md?raw';
import boxVetrineEn from '../content/box_vetrine.en.md?raw';
import boxVetrineEs from '../content/box_vetrine.es.md?raw';
import logo from '../assets/logo.png';
import { useI18n } from '../i18n/i18n';

interface LandingPageProps {
  onLoginClick: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLoginClick }) => {
  const { t, lang } = useI18n();
  // Helper to parse markdown into boxes
  const parseBoxes = (mdRaw: string | undefined) => {
    const md = (mdRaw || '').trim();
    if (!md) return [] as { title: string; description: string }[];
    const lines = md.split(/\r?\n/);
    const results: { title: string; description: string }[] = [];
    let current: { title: string; description: string } | null = null;
    for (const line of lines) {
      const h2 = line.match(/^##\s+(.+)/);
      if (h2) {
        if (current) results.push(current);
        current = { title: h2[1].trim(), description: '' };
      } else if (current) {
        const chunk = line.trim();
        if (chunk.length) current.description += (current.description ? '\n' : '') + chunk;
      }
    }
    if (current) results.push(current);
    return results;
  };

  const boxes = React.useMemo(() => {
    const selectedMd = (lang === 'es' ? boxVetrineEs : boxVetrineEn) || boxVetrineDefault;
    return parseBoxes(selectedMd);
  }, [lang]);

  // Always parse English boxes to preserve icon mapping across languages
  const enBoxes = React.useMemo(() => parseBoxes(boxVetrineEn), []);

  const renderIcon = (title: string) => {
    const t = title.toLowerCase();
    if (t.includes('monitor')) return { bg: 'bg-green-100', jsx: <Activity className="h-8 w-8 text-green-600" /> };
    if (t.includes('pue') || t.includes('analytic') || t.includes('metrics')) return { bg: 'bg-blue-100', jsx: <BarChart3 className="h-8 w-8 text-blue-600" /> };
    if (t.includes('ai') || t.includes('assistant') || t.includes('chat')) return { bg: 'bg-purple-100', jsx: <MessageSquare className="h-8 w-8 text-purple-600" /> };
    if (t.includes('grafana') || t.includes('dashboard')) return { bg: 'bg-orange-100', jsx: <BarChart3 className="h-8 w-8 text-orange-600" /> };
    if (t.includes('database') || t.includes('data')) return { bg: 'bg-indigo-100', jsx: <Database className="h-8 w-8 text-indigo-600" /> };
    if (t.includes('secure') || t.includes('security') || t.includes('shield')) return { bg: 'bg-red-100', jsx: <Shield className="h-8 w-8 text-red-600" /> };
    if (t.includes('performance') || t.includes('optimiz') || t.includes('efficiency')) return { bg: 'bg-yellow-100', jsx: <Zap className="h-8 w-8 text-yellow-600" /> };
    if (t.includes('learning') || t.includes('aprendizaje') || t.includes('advanced')) return { bg: 'bg-emerald-100', jsx: <GraduationCap className="h-8 w-8 text-emerald-600" /> };
    return { bg: 'bg-gray-100', jsx: <Database className="h-8 w-8 text-gray-600" /> };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Hero Section */}
      <div className="container mx-auto px-4 pt-16 pb-24">
        <div className="text-center max-w-4xl mx-auto">
          {/* Logo */}
          <div className="flex items-center justify-center space-x-3 mb-8">
            <img src={logo} alt="Data Evolution logo" className="max-h-14 w-auto" />
            <h1 className="text-4xl font-bold text-gray-900">Data Evolution</h1>
          </div>

          {/* Tagline */}
          <h2 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">{t('landing.tagline')}</h2>

          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">{t('landing.features.subtitle')}</p>

          {/* CTA Button */}
          <button
            onClick={onLoginClick}
            className="inline-flex items-center px-8 py-4 bg-primary-600 text-white text-lg font-semibold rounded-xl hover:bg-primary-700 transition-colors shadow-lg hover:shadow-xl"
          >
            <Shield className="h-5 w-5 mr-2" />
            {t('landing.cta')}
          </button>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">{t('landing.features.title')}</h3>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('landing.features.subtitle')}</p>
        </div>

        {boxes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {boxes.map((box, idx) => {
              const icon = renderIcon(enBoxes[idx]?.title || box.title);
              return (
                <div key={idx} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className={`p-3 ${icon.bg} rounded-xl w-fit mb-6`}>
                    {icon.jsx}
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">{box.title}</h4>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">{box.description}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="p-3 bg-green-100 rounded-xl w-fit mb-6">
                <Activity className="h-8 w-8 text-green-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Real-time Monitoring</h4>
              <p className="text-gray-600 leading-relaxed">Live updates and dashboards.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="p-3 bg-blue-100 rounded-xl w-fit mb-6">
                <BarChart3 className="h-8 w-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">PUE Analytics</h4>
              <p className="text-gray-600 leading-relaxed">Historical analysis and optimization recommendations.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="p-3 bg-purple-100 rounded-xl w-fit mb-6">
                <MessageSquare className="h-8 w-8 text-purple-600" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">AI Assistant</h4>
              <p className="text-gray-600 leading-relaxed">Insights and troubleshooting.</p>
            </div>
          </div>
        )}
      </div>

      {/* Stats Section */}
      <div className="bg-primary-600 py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-2">99.9%</div>
              <div className="text-primary-100">{t('landing.stats.uptime')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">24/7</div>
              <div className="text-primary-100">{t('landing.stats.analytics')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">Multi-vendor</div>
              <div className="text-primary-100">{t('landing.stats.integration')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img src={logo} alt="Data Evolution logo" className="max-h-7 w-auto" />
            <span className="text-lg font-semibold">Data Evolution</span>
          </div>
          <p className="text-sm">{t('landing.footer.version')} v1.0.0</p>
          <p className="text-xs text-gray-500 mt-2">{t('landing.footer.tags')}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
